import { Box, BoxProps, Skeleton, SkeletonProps, Typography } from '@mui/material';
import { clsx } from 'clsx';
import React, { forwardRef, useCallback, useImperativeHandle, useState } from 'react';
import { FormattedMessage } from 'react-intl';

type TVictronVideoProps = React.VideoHTMLAttributes<HTMLVideoElement> & {
    skeletonProps?: SkeletonProps;
    errorProps?: BoxProps;
    ref?: React.Ref<HTMLVideoElement>;
};

export const VictronVideo = forwardRef<{ reset: () => void }, TVictronVideoProps>(
    ({ className, onLoad, onError, skeletonProps, errorProps, ...otherProps }: TVictronVideoProps, ref) => {
        const [isLoading, setIsLoading] = useState(true);
        const [error, setError] = useState(false);

        const handleLoaded: React.ReactEventHandler<HTMLVideoElement> = useCallback(
            (e) => {
                if (onLoad) onLoad(e);
                setError(false);
                setIsLoading(false);
            },
            [onLoad]
        );
        const handleError: React.ReactEventHandler<HTMLVideoElement> = useCallback(
            (e) => {
                if (onError) onError(e);
                setError(true);
                setIsLoading(false);
            },
            [onError]
        );

        const reset = () => {
            setIsLoading(true);
            setError(false);
        };

        useImperativeHandle(ref, () => ({
            reset,
        }));

        return (
            <>
                <Skeleton hidden={!isLoading} variant="rounded" className={className} {...skeletonProps} />
                {!error ? (
                    <video
                        onCanPlayThrough={handleLoaded}
                        onError={handleError}
                        {...otherProps}
                        className={clsx('bg-blog-neutral-300', { hidden: isLoading }, className)}
                    />
                ) : (
                    <Box className={clsx('bg-blog-neutral-300')} {...errorProps}>
                        <Typography>
                            <FormattedMessage id="video.failedToLoad" defaultMessage="Video failed to load" />
                        </Typography>
                    </Box>
                )}
            </>
        );
    }
);
